import { ItemScore } from "@/design-system/ItemScore/ItemScore";
import PlayerCard from "@/design-system/PlayerCard/PlayerCard";
import {
  FIRST_OWNER_GLYPH,
  PLAYER_CARD_BASE_HEIGHT,
  PLAYER_CARD_BASE_WIDTH,
} from "@/design-system/PlayerCard/PlayerCardView";
import { usePriceDisplay } from "@/design-system/Price/usePriceDisplay";
import { convertPlayerPrice } from "@/functions/convertPlayerPrice";
import { cn } from "@/lib/utils";
import { eventTokensSelector } from "@/redux/loadedContent/loadedContentSlice";
import { BREAKPOINTS } from "@/settings";
import type { GallerySolutionItem } from "@repo/shared/api";
import { useTranslation } from "@repo/shared/i18n";
import { Check, Copy } from "lucide-react";
import { ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

// The front page's grid card scale; phones step down so two boxes fit a row.
export const CARD_SCALE = 0.9;
const PHONE_CARD_SCALE = 0.8;
// UltimateTeamIcons glyph.
const CLUB_GLYPH = "";

// How long a copied name or price shows its tick before offering to copy again.
const COPIED_MS = 2000;

type Copied = { resourceId: number; what: "name" | "price" };

const CopiedToast = ({
  label,
  className,
}: {
  label: string;
  className: string;
}) => (
  <span
    className={cn(
      "pointer-events-none absolute whitespace-nowrap rounded-md border border-neutral-700 bg-neutral-900 px-1.5 py-0.5 text-xs text-white shadow-md",
      className,
    )}
  >
    {label}
  </span>
);

const PriceBox = ({
  item,
  copied,
  onCopyPrice,
}: {
  item: GallerySolutionItem;
  copied: boolean;
  onCopyPrice: (price: number) => void;
}) => {
  const { t } = useTranslation("gallery");
  const eventTokens = useSelector(eventTokensSelector);
  const { iconUrl, label, price, showPrice } = usePriceDisplay({
    priceInfo: item.player?.priceInfo,
    objectiveDisplay: item.player?.objectiveDisplay,
    eventTokens,
    assetsBaseUrl: import.meta.env.VITE_APP_ASSETS_BASE_URL,
  });
  // Only a coin price is worth pasting into the market's max buy now.
  const priceInfo = item.player?.priceInfo;
  const copyablePrice =
    priceInfo?.source === "market" && priceInfo.displayPrice > 0
      ? priceInfo.displayPrice
      : null;
  const priceContent = (
    <>
      {iconUrl && <img className="size-3.5 shrink-0" src={iconUrl} alt="" />}
      {label && <span>{label}</span>}
      {showPrice && (
        <span className="font-oswald">{convertPlayerPrice(price, true)}</span>
      )}
    </>
  );
  return (
    <div className="flex min-h-6 items-stretch rounded-lg border border-neutral-700 bg-neutral-900 text-sm tabular-nums">
      <span className="flex items-center border-r border-neutral-700 px-1.5">
        <ItemScore size="small" score={item.score} />
      </span>
      {copyablePrice != null ? (
        <button
          type="button"
          data-own-hover
          title={t("solution.copyPrice")}
          aria-label={t("solution.copyPrice")}
          onClick={() => onCopyPrice(copyablePrice)}
          className="group/price relative flex items-center gap-x-1 rounded-r-lg px-1.5 transition-colors hover:bg-neutral-750 focus-visible:outline-none"
        >
          {priceContent}
          {copied ? (
            <Check className="ml-0.5 size-3.5 shrink-0 text-streamline-success" />
          ) : (
            <Copy className="ml-0.5 size-3.5 shrink-0 text-neutral-400 transition-colors group-hover/price:text-white" />
          )}
          {copied && (
            <CopiedToast
              label={t("solution.priceCopied")}
              className="left-1/2 top-full mt-1 -translate-x-1/2"
            />
          )}
        </button>
      ) : (
        <span className="flex items-center gap-x-1 px-1.5">{priceContent}</span>
      )}
    </div>
  );
};

const Indicator = ({
  glyph,
  glyphClassName,
  label,
  active,
  hoverClassName,
}: {
  glyph: string;
  /** Glyphs differ in size within the font; the slot height keeps labels aligned. */
  glyphClassName: string;
  label: string;
  active: boolean;
  /** The colour it lights up in, under the caller's hover variant. */
  hoverClassName?: string;
}) => (
  <span
    className={cn(
      "flex flex-col items-center gap-1 transition-colors",
      active ? "text-streamline-success" : "text-neutral-600",
      hoverClassName,
    )}
  >
    <span
      className={cn("flex h-5 items-center leading-none", glyphClassName)}
      style={{ fontFamily: "UltimateTeamIcons" }}
    >
      {glyph}
    </span>
    <span
      className="whitespace-nowrap text-xs uppercase leading-none tracking-wide max-[530px]:text-tiny"
      style={{ fontFamily: "CruyffCondensedRegular" }}
    >
      {label}
    </span>
  </span>
);

/** A lineup's player boxes, in the given order. */
export const GallerySolutionView = ({
  items,
  collectedMarks,
  firstOwnerMarks,
  onToggleCollected,
  onToggleFirstOwner,
  footer,
}: {
  items: GallerySolutionItem[];
  /** Cards marked collected / First Owner here, on top of the backend's. */
  collectedMarks: number[];
  firstOwnerMarks: number[];
  onToggleCollected: (resourceId: number) => void;
  /** Without it First Owner only shows. */
  onToggleFirstOwner?: (resourceId: number) => void;
  /** Under the box's marks. */
  footer?: (item: GallerySolutionItem) => ReactNode;
}) => {
  const { t } = useTranslation("gallery");
  const isPhone = useMediaQuery(BREAKPOINTS.MEDIUM2_MOBILE.query);
  const scale = isPhone ? PHONE_CARD_SCALE : CARD_SCALE;
  // Card plus the box's p-3 and border.
  const boxWidth = Math.round(PLAYER_CARD_BASE_WIDTH * scale) + 26;
  // The card and field copied last, while its tick shows.
  const [copied, setCopied] = useState<Copied | null>(null);
  const copy = (resourceId: number, what: Copied["what"], text: string) =>
    navigator.clipboard.writeText(text).then(
      () => setCopied({ resourceId, what }),
      (err) => console.error("Failed to copy:", err),
    );
  useEffect(() => {
    if (copied === null) return;
    const timer = setTimeout(() => setCopied(null), COPIED_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <ul
      className="grid gap-2"
      style={{
        gridTemplateColumns: `repeat(auto-fill, minmax(${boxWidth}px, 1fr))`,
      }}
    >
      {items.map((item) => {
        // The backend's state can't be undone here; marks only add to it.
        const collected =
          item.collected || collectedMarks.includes(item.resourceId);
        const firstOwner =
          item.firstOwner || firstOwnerMarks.includes(item.resourceId);
        const nameCopied =
          copied?.resourceId === item.resourceId && copied.what === "name";
        return (
          <li
            key={item.resourceId}
            className={cn(
              "group/box relative flex flex-col items-center rounded-[20px] border p-3 transition-colors duration-200",
              collected
                ? "border-streamline-success/50 bg-streamline-success/15"
                : "border-neutral-700 bg-neutral-800",
              // The box highlights unless the copy button is the one hovered.
              !item.collected &&
                (collected
                  ? "hover:not-has-[[data-own-hover]:hover]:border-streamline-success/70 hover:not-has-[[data-own-hover]:hover]:bg-streamline-success/20"
                  : "hover:not-has-[[data-own-hover]:hover]:border-neutral-600 hover:not-has-[[data-own-hover]:hover]:bg-neutral-750"),
            )}
          >
            {/* A big hit box in the corner copies the name to search for it in the game. */}
            {item.player && (
              <button
                type="button"
                data-own-hover
                aria-label={`${t("common:copy")} ${item.player.name}`}
                onClick={() =>
                  item.player && copy(item.resourceId, "name", item.player.name)
                }
                className="absolute left-0 top-0 z-20 flex size-14 items-start justify-start rounded-tl-[20px] pl-2.5 pt-2.5 text-white hover:text-neutral-300 focus-visible:outline-none max-[530px]:size-12 max-[530px]:pl-2 max-[530px]:pt-2"
              >
                <span className="relative">
                  {nameCopied ? (
                    <Check className="size-5 text-streamline-success max-[530px]:size-4" />
                  ) : (
                    <Copy className="size-5 max-[530px]:size-4" />
                  )}
                  {/* Beside the icon, over the price pill. */}
                  {nameCopied && (
                    <CopiedToast
                      label={t("solution.nameCopied")}
                      className="left-full top-1/2 ml-1.5 -translate-y-1/2"
                    />
                  )}
                </span>
              </button>
            )}
            {/* The card marks it collected. */}
            <button
              type="button"
              role="checkbox"
              aria-checked={collected}
              aria-label={t("solution.collected")}
              disabled={item.collected}
              onClick={() => onToggleCollected(item.resourceId)}
              className="relative flex w-full justify-center focus-visible:outline-none disabled:cursor-default"
            >
              {/* The price rests on the card's top edge, like the front page's. */}
              {item.player ? (
                <PlayerCard
                  player={item.player}
                  position={item.player.preferredPosition}
                  scale={scale}
                  hidePrice
                  firstOwner={firstOwner}
                />
              ) : (
                <div
                  className="rounded-lg bg-neutral-800"
                  style={{
                    width: PLAYER_CARD_BASE_WIDTH * scale,
                    height: PLAYER_CARD_BASE_HEIGHT * scale,
                  }}
                />
              )}
            </button>
            {/* Over the card's top edge, outside its button so the price can be its own. */}
            <div className="pointer-events-none absolute inset-x-0 top-5 z-10 flex justify-center *:pointer-events-auto">
              <PriceBox
                item={item}
                copied={
                  copied?.resourceId === item.resourceId &&
                  copied.what === "price"
                }
                onCopyPrice={(price) =>
                  copy(item.resourceId, "price", String(Math.round(price)))
                }
              />
            </div>
            {/* Green on a collected box, where neutral lines fade into the tint. */}
            <div
              className={cn(
                "mt-2 grid w-full grid-cols-2 divide-x border-t pt-2 transition-colors duration-200 *:transition-colors *:duration-200",
                collected
                  ? "divide-streamline-success/40 border-streamline-success/40"
                  : "divide-neutral-700 border-neutral-700 group-hover/box:not-group-has-[[data-own-hover]:hover]/box:divide-neutral-600 group-hover/box:not-group-has-[[data-own-hover]:hover]/box:border-neutral-600",
              )}
            >
              <button
                type="button"
                role="checkbox"
                aria-checked={firstOwner}
                aria-label={t("solution.firstOwner")}
                disabled={item.firstOwner || !onToggleFirstOwner}
                onClick={() => onToggleFirstOwner?.(item.resourceId)}
                className="group/indicator focus-visible:outline-none disabled:cursor-default"
              >
                <Indicator
                  glyph={FIRST_OWNER_GLYPH}
                  glyphClassName="text-base"
                  label={t("solution.firstOwner")}
                  active={firstOwner}
                  hoverClassName={
                    item.firstOwner || !onToggleFirstOwner
                      ? undefined
                      : firstOwner
                        ? "group-hover/indicator:text-streamline-success/70"
                        : "group-hover/indicator:text-neutral-400"
                  }
                />
              </button>
              <button
                type="button"
                role="checkbox"
                aria-checked={collected}
                aria-label={t("solution.collected")}
                disabled={item.collected}
                onClick={() => onToggleCollected(item.resourceId)}
                className="focus-visible:outline-none disabled:cursor-default"
              >
                <Indicator
                  glyph={CLUB_GLYPH}
                  glyphClassName="text-xl"
                  label={t("solution.collected")}
                  active={collected}
                  // Every click in the box but the copy one marks the card collected.
                  hoverClassName={
                    item.collected
                      ? undefined
                      : collected
                        ? "group-hover/box:not-group-has-[[data-own-hover]:hover]/box:text-streamline-success/70"
                        : "group-hover/box:not-group-has-[[data-own-hover]:hover]/box:text-neutral-400"
                  }
                />
              </button>
            </div>
            {footer?.(item)}
          </li>
        );
      })}
    </ul>
  );
};
